import React from 'react';
import { ButtonRed, SignOutButton } from '../buttons';
import { AccountLink } from './AccountLink';
import { SimpleDialog } from '../SimpleDialog';
import { PatientDoc } from '@usacsmideo/types';
import usePatientDoc from '../../hooks/usePatientDoc';
import { updateConciergeNavigatorField } from '../../data/conciergeNavigator/updateConciergeNavigatorField';
// import { HamburgerMenu } from './HamburgerMenu';
import { useNetworkState } from '@uidotdev/usehooks';
import { Community, HeadsetHelp, Home, ScanQrCode, Wifi, WifiWarning, WifiXmark } from 'iconoir-react';
import { useNavigate } from '@tanstack/react-router';

export const Header = (props: { patientId: string }) => {
  const network = useNetworkState();
  const { patientId } = props;
  const patientDocQuery = usePatientDoc(patientId, { enabled: !!patientId && patientId.length > 3 });
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [open, setOpen] = React.useState(false);
  const [callLive, setCallLive] = React.useState(false);
  const [showAlert, setShowAlert] = React.useState(false);
  const [networkStatus, setNetworkStatus] = React.useState('online');

  // Track if we've done the initial alert check
  const hasCheckedInitialAlert = React.useRef(false);

  const handleActionClick = () => {
    window.open('mailto:info@mideohealth.com');
    setOpen(false);
  };
  const navigate = useNavigate();

  React.useEffect(() => {
    if (networkStatus === 'offline' && network.online === true) {
      setNetworkStatus('unstable');
      return;
    }
    if (networkStatus === 'online' && network.online === true) {
      setNetworkStatus('online');
      return;
    }
    if (network.online === false) {
      setNetworkStatus('offline');
      return;
    }
  }, [network, networkStatus]);

  // Handle telehealthCallActive changes (for button visibility)
  React.useEffect(() => {
    setCallLive(patientInfo?.conciergeNavigator?.telehealthCallActive === true);
  }, [patientInfo?.conciergeNavigator?.telehealthCallActive]);

  // Handle telehealthShowAlert changes (for alert dialog)
  React.useEffect(() => {
    if (!patientInfo?.conciergeNavigator) return;

    const shouldShowAlert = patientInfo.conciergeNavigator.telehealthShowAlert === true;

    // On initial load
    if (!hasCheckedInitialAlert.current) {
      hasCheckedInitialAlert.current = true;
      setShowAlert(shouldShowAlert);
      return;
    }

    // On subsequent updates (from admin console or elsewhere)
    setShowAlert(shouldShowAlert);
  }, [patientInfo?.conciergeNavigator?.telehealthShowAlert]);

  const handleTelehealthCall = () => {
    // Close the alert dialog
    setShowAlert(false);

    // Update Firestore to persist that the user dismissed the alert
    updateConciergeNavigatorField({
      patientId,
      field: 'telehealthShowAlert',
      value: false,
    }).catch((error) => {
      console.error('Failed to update telehealthShowAlert:', error);
    });

    // Open the telehealth call URL
    if (window && patientInfo.conciergeNavigator?.telehealthCallURL) {
      //@ts-expect-error - type checking for global Window not working in previous line?
      window.open(patientInfo.conciergeNavigator.telehealthCallURL, '_blank').focus();
    }
  };

  const handleCallDialogClose = (shouldClose: boolean) => {
    if (shouldClose === false) {
      setShowAlert(false);

      // Update Firestore to persist that the user dismissed the alert
      updateConciergeNavigatorField({
        patientId,
        field: 'telehealthShowAlert',
        value: false,
      }).catch((error) => {
        console.error('Failed to update telehealthShowAlert:', error);
      });
    }
  };

  return (
    <>
      {/* CU-86ajd3yt0: made responsive. Previously the left icon group was a
          plain (non-flex) div and the network/right groups used fixed ml-16
          margins, so on a phone-width viewport the icons overflowed the h-12 bar
          and spilled onto the page. Now: h-full flex row that contains its
          content, flex icon groups with gap (no per-button ml-4), space-between
          spacing (no fixed margins), and the network-status TEXT hidden on small
          screens (the colored wifi icon still conveys status). */}
      <div className="bg-mideo-darkred h-12 w-full px-4 sm:px-8">
        <div
          id="header-container"
          className="flex h-full flex-row items-center justify-between gap-2"
        >
          <div className="flex flex-row items-center gap-2 sm:gap-4">
            {/* // TODO: RESTORE HAMBURGER MENU WHEN READY */}
            {/* {patientInfo && <HamburgerMenu navObj={patientInfo.navObj} />} */}
            <button
              onClick={() => navigate({ to: '/$patientId', params: { patientId } })}
              style={{ cursor: 'pointer' }}
              aria-label="Home"
            >
              <Home height={'2em'} width={'2em'} color="#fff" />
            </button>
            {/* ECN Subtask 9: top-level My MIDEO Family entry (static, not navObj). */}
            <button
              onClick={() => navigate({ to: '/myMideoFamily' })}
              style={{ cursor: 'pointer' }}
              aria-label="My MIDEO Family"
            >
              <Community height={'2em'} width={'2em'} color="#fff" />
            </button>
            {/* ECN Sessions Surface (CU-86aj1c9ev): top-level MIDEO Scan Activity
                entry. A patient may also be an agent, so the destination shows
                their own scans AND any family scans — the umbrella label is
                honest about that. Same route + label as the agent shell. */}
            <button
              onClick={() => navigate({ to: '/sessions' })}
              style={{ cursor: 'pointer' }}
              aria-label="MIDEO Scan Activity"
            >
              <ScanQrCode height={'2em'} width={'2em'} color="#fff" />
            </button>
          </div>
          {callLive && (
            <div>
              <ButtonRed id="join-call-button" type="button" textContent="Join Call" onClick={handleTelehealthCall} />
            </div>
          )}
          {networkStatus === 'offline' && (
            <div className="flex flex-row items-center gap-1">
              <WifiXmark height={'2em'} width={'2em'} color="#f56565" />
              <div className="hidden font-bold text-red-500 sm:block">Offline</div>
            </div>
          )}
          {networkStatus === 'unstable' && (
            <div className="flex flex-row items-center gap-1">
              <WifiWarning height={'2em'} width={'2em'} color="#FFEB3B" />
              <div className="hidden font-bold text-yellow-500 sm:block">Unstable Connection</div>
            </div>
          )}
          {networkStatus === 'online' && (
            <div className="flex flex-row items-center gap-1">
              <Wifi height={'2em'} width={'2em'} color="#48bb78" />
              <p className="hidden font-bold text-green-500 sm:block">Online</p>
            </div>
          )}
          <div className="flex flex-row items-center gap-2 sm:gap-4">
            <button onClick={() => setOpen(true)} style={{ cursor: 'pointer' }} aria-label="Need help?">
              <HeadsetHelp height={'2em'} width={'2em'} color="#fff" />
            </button>
            <AccountLink />
            <SignOutButton />
          </div>
        </div>
      </div>
      <SimpleDialog
        open={open}
        setOpen={setOpen}
        title="Need Help?"
        content="Contact us by phone at 844-MIDEO-4U or click the button below to send us an email."
        actionButtonTitle="Send Email"
        actionButtonHandler={handleActionClick}
      />
      <SimpleDialog
        open={showAlert}
        setOpen={handleCallDialogClose}
        title="Your Appointment is Live!"
        content="Click the button below to join the call in a new tab."
        actionButtonTitle="Join Call"
        actionButtonHandler={handleTelehealthCall}
      />
    </>
  );
};
